import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/system';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldComponentProps {
  label: string;
  value: string | number | null | undefined;
  onChange: (value: string | number) => void;
  onSelect?: (value: string | number, label: string) => void;
  options: readonly SelectOption[] | readonly string[] | readonly number[];
  error?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  placeholder?: string;
  multiple?: boolean;
  loading?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * SelectFieldComponent - Reusable Select Dropdown Component
 * 
 * A standardized select field component that provides:
 * - Consistent styling and behavior across the application
 * - Support for both simple arrays and complex option objects
 * - Built-in error handling and display
 * - Support for disabled options
 * - Multiple selection support
 * - Flexible sizing and layout options
 */
export const SelectFieldComponent: React.FC<SelectFieldComponentProps> = ({
  label,
  value,
  onChange,
  onSelect,
  options,
  error,
  fullWidth = true,
  disabled = false,
  required = false,
  size = 'medium',
  variant = 'outlined',
  // placeholder,
  multiple = false,
  loading = false,
  sx,
}) => {
  const normalizedOptions: SelectOption[] = options.map((option) => {
    if (typeof option === 'string' || typeof option === 'number') {
      return { value: option, label: String(option) };
    }
    return option;
  });

  return (
    <FormControl
      fullWidth={fullWidth}
      error={!!error}
      disabled={disabled || loading}
      required={required}
      size={size}
      variant={variant}
      sx={sx}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value || (multiple ? [] : '')}
        onChange={(e) => {
          const selectedValue = e.target.value as string | number;
          onChange(selectedValue);
          if (onSelect) {
            const selectedOption = normalizedOptions.find(o => String(o.value) === String(selectedValue));
            if (selectedOption) onSelect(selectedValue, selectedOption.label);
          }
        }}
        label={label}
        multiple={multiple}
        displayEmpty={true}
        sx={{
          backgroundColor: "#f9fafb", // bg-gray-50
          borderRadius: "0.5rem",     // rounded-lg
          fontSize: "0.875rem",       // text-sm
          color: "#111827",           // text-gray-900
          "& .MuiOutlinedInput-notchedOutline": {
           borderColor: "#d1d5db",   // border-gray-300
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#9ca3af",   // slightly darker on hover
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "#3b82f6",   // primary-500
          boxShadow: "0 0 0 1px #3b82f6", // focus:ring-primary-500
        },
        "& .MuiSelect-select": {
          padding: "0.625rem 0.75rem", // p-2.5
        },}}
      >
        {normalizedOptions.map((option) => (
          <MenuItem 
            key={String(option.value)} 
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default SelectFieldComponent;